package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import uz.mint.frontend.EyebrowStyle
import uz.mint.frontend.PageGutter
import uz.mint.frontend.SiteConfig
import uz.mint.frontend.alignItems
import uz.mint.frontend.api.AccountApi
import uz.mint.frontend.api.PaymentOption
import uz.mint.frontend.api.Plan
import uz.mint.frontend.api.PlanDetailState
import uz.mint.frontend.api.SessionState
import uz.mint.frontend.api.rememberSession
import uz.mint.frontend.api.dataLabel
import uz.mint.frontend.api.flag
import uz.mint.frontend.api.formatUzs
import uz.mint.frontend.api.priceLabel
import uz.mint.frontend.api.rememberPlanDetail
import uz.mint.frontend.api.validityLabel
import uz.mint.frontend.components.widgets.CloseIcon
import uz.mint.frontend.components.widgets.SecondaryButton
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.justifyContent
import uz.mint.frontend.marginInlineAuto
import uz.mint.frontend.toSitePalette

/**
 * Checkout sheet for one tariff: what you get, what it costs, and how to pay.
 *
 * A dialog rather than a route. The plan grid is paginated and filtered, so navigating away and
 * back would lose the page the customer was on; the URL also has nothing to say about a purchase
 * that has not happened yet.
 *
 * Prices are re-read here rather than carried over from the card ([rememberPlanDetail]): the grid
 * may have been rendered minutes ago, or baked into a static export hours ago, and the figure
 * next to a pay button has to be the one that will actually be charged.
 *
 * The pay buttons hand off to a real cashier against a real order: the backend mints the order,
 * freezes its price and returns one link per configured provider, and the eSIM is issued
 * automatically once that provider's callback confirms the payment. Telegram remains below them
 * as the route for anything that goes wrong, not as the way a purchase is normally completed.
 */
@Composable
fun CheckoutDialog(locale: Locale, strings: SiteStrings, plan: Plan, onClose: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    val (state, retry) = rememberPlanDetail(locale, plan.packageCode)

    Div(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(95)
            .backgroundColor(palette.paper).color(palette.ink)
            .overflow { y(Overflow.Auto) }
            .toAttrs {
                attr("role", "dialog")
                attr("aria-modal", "true")
                attr("aria-label", strings.checkoutTitle)
            }
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .position(Position.Sticky).top(0.px).zIndex(2)
                .backgroundColor(palette.paper)
                .borderBottom(2.px, LineStyle.Solid, palette.rule)
                .gap(20.px).padding(topBottom = 16.px, leftRight = PageGutter),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                strings.checkoutTitle,
                Modifier.fontSize(clamp(18.px, 2.vw, 26.px)).fontWeight(FontWeight.ExtraBold)
                    .letterSpacing((-0.025).em),
            )
            Spacer()
            Button(
                attrs = Modifier
                    .size(46.px)
                    .border(2.px, LineStyle.Solid, palette.rule)
                    .display(DisplayStyle.Grid)
                    .justifyContent("center").alignItems("center")
                    .flexShrink(0)
                    .backgroundColor(Colors.Transparent)
                    .cursor(Cursor.Pointer)
                    .onClick { onClose() }
                    .toAttrs { attr("aria-label", strings.legalClose) }
            ) { CloseIcon(color = palette.ink) }
        }

        Column(
            Modifier
                .fillMaxWidth().maxWidth(720.px).marginInlineAuto()
                .padding(top = clamp(24.px, 3.vw, 44.px), bottom = 72.px, leftRight = PageGutter)
                .gap(24.px)
        ) {
            // Rendered from the card's own data so the sheet has content the instant it opens;
            // the fetch below only adds the payment options and the confirmed sum.
            PlanSummary(locale, strings, plan)

            when (state) {
                is PlanDetailState.Loading -> SpanText(
                    strings.checkoutLoading,
                    Modifier.color(palette.ink2).fontSize(15.px),
                )

                is PlanDetailState.Failed -> Column(Modifier.gap(14.px)) {
                    SpanText(strings.checkoutError, Modifier.color(palette.ink2).fontSize(15.px))
                    Row(Modifier.gap(12.px).flexWrapCss()) {
                        SecondaryButton(strings.tariffsRetry, retry)
                        TelegramLink(strings, plan, palette.rule, palette.ink)
                    }
                }

                is PlanDetailState.Loaded -> PaymentOptions(locale, strings, state, plan)
            }
        }
    }
}

/** Everything the customer is buying, before anything about paying for it. */
@Composable
private fun PlanSummary(locale: Locale, strings: SiteStrings, plan: Plan) {
    val palette = ColorMode.current.toSitePalette()

    Column(
        Modifier.fillMaxWidth().border(2.px, LineStyle.Solid, palette.rule)
            .backgroundColor(palette.card)
    ) {
        Row(
            Modifier.fillMaxWidth().gap(14.px)
                .padding(topBottom = 18.px, leftRight = 20.px)
                .borderBottom(2.px, LineStyle.Solid, palette.rule),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(plan.flag(), Modifier.fontSize(34.px).lineHeight(1))
            Column(Modifier.gap(4.px)) {
                SpanText(
                    plan.name,
                    Modifier.fontSize(19.px).fontWeight(FontWeight.ExtraBold)
                        .letterSpacing((-0.02).em).lineHeight(1.2),
                )
                plan.region?.let {
                    SpanText(it, Modifier.fontSize(13.px).color(palette.ink2))
                }
            }
        }

        SummaryRow(strings.checkoutData, plan.dataLabel(strings))
        SummaryRow(strings.checkoutValidity, plan.validityLabel(locale, strings))
        SummaryRow(strings.checkoutCode, plan.packageCode, mono = true)
    }
}

@Composable
private fun SummaryRow(label: String, value: String, mono: Boolean = false) {
    val palette = ColorMode.current.toSitePalette()
    Row(
        Modifier.fillMaxWidth().gap(16.px)
            .padding(topBottom = 13.px, leftRight = 20.px)
            .borderBottom(2.px, LineStyle.Solid, palette.rule),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(label, Modifier.fontSize(14.px).color(palette.ink2))
        Spacer()
        SpanText(
            value,
            Modifier.fontSize(15.px).fontWeight(FontWeight.Bold).textAlign(TextAlign.End)
                .then(if (mono) Modifier.fontFamily("ui-monospace", "SFMono-Regular", "monospace") else Modifier),
        )
    }
}

/**
 * The total, then the ways to pay it.
 *
 * Paying requires a signed-in customer, and that is not a gate for its own sake: both cashiers'
 * callbacks name an order, and an order has to belong to someone or a confirmed payment has no
 * one to give the eSIM to. A logged-out visitor is shown why and sent to sign in, keeping the
 * plan they picked in the URL so they land back on it.
 *
 * **Which cashiers are on offer is only known after the order exists**, because each link
 * carries that order's reference. So the first press creates the order, and only then — and only
 * when there is genuinely more than one — is a choice shown. With a single cashier configured
 * this stays exactly one press, which is what it was before Payme was added.
 */
@Composable
private fun PaymentOptions(
    locale: Locale,
    strings: SiteStrings,
    state: PlanDetailState.Loaded,
    plan: Plan,
) {
    val palette = ColorMode.current.toSitePalette()
    val session = rememberSession()
    val scope = rememberCoroutineScope()
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var cashiers by remember { mutableStateOf<List<PaymentOption>>(emptyList()) }

    Column(Modifier.fillMaxWidth().gap(20.px)) {
        Row(
            Modifier.fillMaxWidth().gap(16.px)
                .borderTop(2.px, LineStyle.Solid, palette.rule).padding(top = 18.px),
            verticalAlignment = Alignment.Bottom,
        ) {
            Column(Modifier.gap(4.px)) {
                SpanText(
                    strings.checkoutTotal,
                    EyebrowStyle.toModifier().fontSize(11.px).letterSpacing(0.1.em).opacity(0.6),
                )
                // Re-read a moment ago by [rememberPlanDetail], so this is the price the order
                // will be created at rather than whatever the card was rendered with.
                SpanText(
                    state.detail.plan.priceLabel(strings),
                    Modifier.fontSize(clamp(26.px, 3.vw, 34.px)).fontWeight(FontWeight.ExtraBold)
                        .letterSpacing((-0.035).em).lineHeight(1),
                )
            }
            Spacer()
        }

        SpanText(
            strings.checkoutHandoff,
            Modifier.fontSize(14.px).lineHeight(1.55).color(palette.ink2),
        )

        Column(Modifier.fillMaxWidth().gap(12.px)) {
            when (session) {
                // Still resolving. Rendering either branch would flicker, and the pay button is
                // the one control on this sheet that must not move under a tapping finger.
                SessionState.Loading -> SpanText(
                    strings.checkoutLoading,
                    Modifier.fontSize(14.px).color(palette.ink2),
                )

                SessionState.Anonymous -> {
                    SpanText(
                        strings.checkoutSignInNote,
                        Modifier.fontSize(13.px).lineHeight(1.5).color(palette.ink2),
                    )
                    Link(
                        // Back to the catalogue afterwards, not to My eSIMs: they were mid-purchase.
                        path = "${locale.loginPath}?next=${locale.plansPath}",
                        modifier = Modifier
                            .fillMaxWidth()
                            .backgroundColor(palette.solidBg).color(palette.solidFg)
                            .fontWeight(FontWeight.Bold).fontSize(16.px)
                            .padding(topBottom = 17.px, leftRight = 26.px)
                            .textAlign(TextAlign.Center),
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    ) { SpanText(strings.checkoutSignIn) }
                }

                is SessionState.SignedIn -> if (cashiers.isEmpty()) {
                    PayButton(
                        label = if (busy) strings.checkoutPaying else strings.checkoutProceed,
                        enabled = !busy && state.detail.plan.priceUzs != null,
                        palette = palette,
                    ) {
                        busy = true
                        error = null
                        scope.launch {
                            runCatching { AccountApi.createOrder(plan.packageCode, locale.code) }
                                .onSuccess { created ->
                                    when (created.options.size) {
                                        // No cashier configured: the order exists, so say what is
                                        // actually true rather than pretending the button failed.
                                        0 -> {
                                            error = strings.checkoutUnavailable
                                            busy = false
                                        }
                                        // Nothing to choose between — skip the extra press.
                                        // `assign` keeps a history entry, so the browser's back
                                        // button returns here.
                                        1 -> window.location.assign(created.options.first().payUrl)
                                        else -> {
                                            cashiers = created.options
                                            busy = false
                                        }
                                    }
                                }
                                .onFailure {
                                    error = strings.checkoutOrderError
                                    busy = false
                                }
                        }
                    }
                } else {
                    SpanText(
                        strings.checkoutChoose,
                        Modifier.fontSize(13.px).lineHeight(1.5).color(palette.ink2),
                    )
                    cashiers.forEach { option ->
                        PayButton(
                            label = strings.checkoutPay.replace("{gateway}", option.name),
                            enabled = true,
                            palette = palette,
                        ) { window.location.assign(option.payUrl) }
                    }
                }
            }

            error?.let {
                SpanText(
                    it,
                    Modifier.fontSize(13.px).lineHeight(1.5).color(palette.ink2).opacity(0.85),
                )
            }

            TelegramLink(strings, plan, palette.rule, palette.ink)
        }
    }
}

@Composable
private fun PayButton(
    label: String,
    enabled: Boolean,
    palette: uz.mint.frontend.SitePalette,
    onClick: () -> Unit,
) {
    Button(
        attrs = Modifier
            .fillMaxWidth()
            // Disabled is a pale surface, not a fill of `rule` — see PrimaryAction in
            // LoginPage: `rule` is near-black in the light palette, so `ink2` on it was a
            // 1.29 contrast and the label vanished in one theme out of two.
            .backgroundColor(if (enabled) palette.solidBg else palette.paper2)
            .color(if (enabled) palette.solidFg else palette.ink2)
            .opacity(if (enabled) 1.0 else 0.7)
            .border(if (enabled) 0.px else 2.px, if (enabled) LineStyle.None else LineStyle.Solid, palette.rule)
            .fontWeight(FontWeight.Bold).fontSize(16.px).fontFamily("inherit")
            .padding(topBottom = 17.px, leftRight = 26.px)
            .textAlign(TextAlign.Center)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .onClick { if (enabled) onClick() }
            .toAttrs { if (!enabled) attr("disabled", "") }
    ) { SpanText(label) }
}

/**
 * The always-available route: Telegram, carrying the package code so whoever answers knows which
 * plan was tapped.
 */
@Composable
private fun TelegramLink(
    strings: SiteStrings,
    plan: Plan,
    rule: com.varabyte.kobweb.compose.ui.graphics.Color,
    ink: com.varabyte.kobweb.compose.ui.graphics.Color,
) {
    Link(
        path = "${SiteConfig.TELEGRAM_URL}?text=${plan.packageCode}",
        modifier = Modifier
            .fillMaxWidth()
            .border(2.px, LineStyle.Solid, rule)
            .color(ink)
            .fontWeight(FontWeight.Bold).fontSize(16.px)
            .padding(topBottom = 15.px, leftRight = 24.px)
            .textAlign(TextAlign.Center),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    ) { SpanText(strings.checkoutTelegram) }
}
